/**
 * Chips de variante na revisão do import (#474, bloqueante da revisão UX).
 * Variante sem preço reconhecido (0,00/PSM — o commit vai pausá-la) mostra o
 * selo «Confirmar preço» e um campo pequeno para o dono pôr o preço: só com
 * price_cents > 0 a variante é gravada disponível e servida no Menu público.
 * O aviso fala da OPÇÃO («esta opção não aparece»), não do prato inteiro
 * (UX r2, ponto 5). Variante com preço fica no chip de sempre.
 */
import type { ImportDraftProduct } from '../lib/api.js'
import { useI18n } from '../lib/i18n.js'
import { formatPriceCents, ImportReviewPriceInput } from './ImportReviewPriceInput.js'
import { ImportReviewPriceBadge, needsPriceConfirm } from './ImportReviewPriceBadge.js'

export function ImportReviewVariants({
  variants,
  ci,
  pi,
  currency,
  onVariantsChange,
}: {
  variants: ImportDraftProduct['variants']
  ci: number
  pi: number
  /** moeda do import — decimais do display (JPY 0, KWD 3…) */
  currency?: string | null | undefined
  onVariantsChange: (variants: ImportDraftProduct['variants']) => void
}) {
  const { t, lang } = useI18n()
  return (
    <>
      {variants.map((v, vi) => {
        const unpriced = needsPriceConfirm(v)
        const badgeId = `import-review-variant-price-confirm-${ci}-${pi}-${vi}`
        // UX QA r7: a variante com preço TAMBÉM é editável (uma gralha
        // corrige-se ali mesmo) — muda só o estilo/aviso do chip
        return (
          <span
            key={vi}
            data-testid={`import-review-variant-${unpriced ? 'unpriced' : 'chip'}-${ci}-${pi}-${vi}`}
            // UX r3 ponto 11: chip que quebra linha usa rounded-xl (pílula
            // rounded-full de várias linhas fica estranha a 360 px)
            className={`inline-flex flex-wrap items-center gap-1 min-w-0 py-0.5 ${
              unpriced
                ? 'rounded-xl bg-amber-50 border border-amber-200 ps-2 pe-1'
                : 'rounded-full bg-indigo-50 text-indigo-700 px-2'
            }`}
          >
            <bdi
              dir="auto"
              className={unpriced ? 'text-xs text-amber-900' : 'text-xs'}
              title={unpriced ? undefined : t('admin.import.variants')}
            >
              {v.name}
              {!unpriced && ` · ${formatPriceCents(v.price_cents, currency, lang)}`}
            </bdi>
            {unpriced && (
              <>
                <ImportReviewPriceBadge product={v} id={badgeId} withHint={false} scope="option" />
                {/* UX r2 ponto 5: aviso VISÍVEL da opção (não do prato) */}
                <span className="basis-full text-[10px] text-amber-800">
                  {t('admin.import.price_confirm_hint_option')}
                </span>
              </>
            )}
            <ImportReviewPriceInput
              cents={v.price_cents}
              small
              currency={currency}
              testId={`import-review-variant-price-${ci}-${pi}-${vi}`}
              label={t('admin.import.price_of', { name: v.name })}
              describedBy={unpriced ? badgeId : undefined}
              onCommit={(cents) =>
                onVariantsChange(
                  variants.map((x, xi) => (xi === vi ? { ...x, price_cents: cents } : x)),
                )
              }
            />
          </span>
        )
      })}
    </>
  )
}
